/**
 * Universal date/time utilities for converting UTC to local timezone
 * All date formatting should use these functions for consistency
 */

import { format, parseISO, formatDistanceToNow } from 'date-fns'

/**
 * Parse a UTC timestamp string and return a local Date object
 * Handles both ISO strings and Date objects
 */
export const parseUTCDate = (dateString: string | Date | null | undefined): Date | null => {
  if (!dateString) return null

  try {
    if (dateString instanceof Date) {
      return dateString
    }

    // parseISO automatically handles timezone conversion to local
    return parseISO(dateString)
  } catch (error) {
    console.error('Error parsing date:', dateString, error)
    return null
  }
}

/**
 * Format a UTC timestamp to local time with a custom format
 * @param dateString - UTC timestamp string from API
 * @param formatString - date-fns format string (default: 'MMM d, yyyy HH:mm')
 * @returns Formatted date string in local timezone
 */
export const formatUTCToLocal = (
  dateString: string | Date | null | undefined,
  formatString: string = 'MMM d, yyyy HH:mm'
): string => {
  const date = parseUTCDate(dateString)
  if (!date) return 'N/A'

  try {
    return format(date, formatString)
  } catch (error) {
    console.error('Error formatting date:', dateString, error)
    return 'Invalid Date'
  }
}

/**
 * Format a UTC timestamp to local date only (no time)
 */
export const formatDateOnly = (dateString: string | Date | null | undefined): string => {
  return formatUTCToLocal(dateString, 'MMM d, yyyy')
}

/**
 * Format a UTC timestamp to local time only (no date)
 */
export const formatTimeOnly = (dateString: string | Date | null | undefined): string => {
  return formatUTCToLocal(dateString, 'HH:mm:ss')
}

/**
 * Format a UTC timestamp to full local datetime with timezone
 */
export const formatDateTimeWithZone = (dateString: string | Date | null | undefined): string => {
  return formatUTCToLocal(dateString, 'MMM d, yyyy HH:mm:ss zzz')
}

/**
 * Format a UTC timestamp to relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString: string | Date | null | undefined): string => {
  const date = parseUTCDate(dateString)
  if (!date) return 'N/A'

  try {
    return formatDistanceToNow(date, { addSuffix: true })
  } catch (error) {
    console.error('Error formatting relative time:', dateString, error)
    return 'Invalid Date'
  }
}

/**
 * Format for chart timestamps (short format)
 */
export const formatChartTimestamp = (dateString: string | Date): string => {
  return formatUTCToLocal(dateString, 'MMM dd HH:mm')
}

/**
 * Format for data export (ISO format in local timezone)
 */
export const formatForExport = (dateString: string | Date | null | undefined): string => {
  return formatUTCToLocal(dateString, "yyyy-MM-dd'T'HH:mm:ss")
}

/**
 * Format for detailed display (includes day of week)
 */
export const formatDetailed = (dateString: string | Date | null | undefined): string => {
  return formatUTCToLocal(dateString, 'EEEE, MMM d, yyyy \'at\' HH:mm')
}

/**
 * Get the current user's timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Common date format presets
 */
export const DATE_FORMATS = {
  SHORT: 'MMM d, yyyy',
  LONG: 'MMMM d, yyyy',
  WITH_TIME: 'MMM d, yyyy HH:mm',
  WITH_SECONDS: 'MMM d, yyyy HH:mm:ss',
  WITH_TIMEZONE: 'MMM d, yyyy HH:mm zzz',
  TIME_ONLY: 'HH:mm',
  CHART: 'MMM dd HH:mm',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
  DETAILED: 'EEEE, MMM d, yyyy \'at\' HH:mm'
} as const
